/**
 * Multiplayer Orchestrator — wires PartyKit networking into the game loop.
 *
 * This module is the single integration point between the game code and
 * the networking layer. It handles:
 * - Creating/joining rooms
 * - Lobby state management (Create/Join flow, room code entry)
 * - Input routing (local vs network based on active player)
 * - Sending key states per frame during your turn
 * - State hash validation at turn boundaries
 * - Resync snapshot send/receive on desync
 *
 * Usage in main.ts:
 *   const mp = new MultiplayerManager(state);
 *   mp.init();
 *   // In fixed update:
 *   const input = mp.getInputForFrame();
 *   mp.onPostUpdate(fx);
 */

import PartySocket from 'partysocket';
import { GameClient } from './client';
import { captureKeyState } from './input-capture';
import { hashGameState } from './sync';
import { ROUND_START } from '../constants';
import { NetworkInput } from '../input';
import type { GameState } from '../types';
import type { GameEffect } from '../game/effects';
import type { InputProvider } from '../input';
import { localInput } from '../input';
import type { Renderers } from '../game/effects';
import { registerConnectionHandlers } from './handlers-connection';
import { registerGameplayHandlers, applyTurnSnapshotData } from './handlers-gameplay';
import { processOutboundEffects, type OutboundDeps } from './handlers-outbound';
import type { NetDebugLog } from './debug-log';
import type { TurnSnapshotPlayer } from './messages';

// PartyKit host — use localhost for dev, deployed URL for production
const PARTY_HOST = import.meta.env.DEV
    ? 'localhost:1999'
    : (import.meta.env.VITE_PARTY_HOST || 'geometry-blast.clokk.partykit.dev');

/** Aim update send rate limiter — send every N frames (~10Hz at 60fps). */
const AIM_SEND_INTERVAL = 2; // send aim every 2 frames (~30Hz) for smooth aim line preview

export class MultiplayerManager {
    readonly client = new GameClient();
    readonly networkInput = new NetworkInput();
    readonly recoveryInput = new NetworkInput();
    private state: GameState;
    private aimSendCounter = 0;
    private lastSentAim = { x: 0, y: 0, angle: 0, power: 0, shotTier: 1, momentum: 0 };
    private lastGrapplePhase: string | null = null; // track grapple phase transitions
    private renderers: Renderers | null = null;

    // Matchmaker (quickplay)
    private matchmakerSocket: PartySocket | null = null;

    // Track lobby state to detect transitions
    private prevLobbyAction: string | null = null;
    private prevLobbyStatus: string | null = null;
    private keyboardListener: ((e: KeyboardEvent) => void) | null = null;
    private mpWaitUntilFrame = -1; // frame-based delay for mp_waiting → aiming transition
    private preFireSnapshot = { x: 0, y: 0, angle: 0, power: 0 };
    private pendingRoomSeed = 0;
    private disconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;
    private connectRetryTimer: ReturnType<typeof setTimeout> | null = null;
    private debugLog: NetDebugLog | null = null;
    private pendingCheckpointId = -1; // deferred hash: non-turn-owner waits for turn_snapshot
    private bufferedTurnSnapshot: TurnSnapshotPlayer[] | null = null; // snapshot that arrived before advanceTurn
    private outboundDeps: OutboundDeps;

    constructor(state: GameState) {
        this.state = state;
        this.setupClientHandlers();
        this.outboundDeps = this.buildOutboundDeps();
    }

    /** Set renderers reference for applying effects during multiplayer reset. */
    setRenderers(renderers: Renderers): void {
        this.renderers = renderers;
    }

    /** Set debug log for netcode debugging (DEV-only). */
    setDebugLog(log: NetDebugLog): void {
        this.debugLog = log;
    }

    /**
     * Initialize keyboard listener for room code text entry.
     * Call once after construction.
     */
    init(): void {
        this.keyboardListener = (e: KeyboardEvent) => {
            this.onKeyboardInput(e);
        };
        window.addEventListener('keydown', this.keyboardListener);
    }

    /** Clean up. */
    destroy(): void {
        if (this.keyboardListener) {
            window.removeEventListener('keydown', this.keyboardListener);
            this.keyboardListener = null;
        }
        if (this.disconnectGraceTimer) {
            clearTimeout(this.disconnectGraceTimer);
            this.disconnectGraceTimer = null;
        }
        if (this.connectRetryTimer) {
            clearTimeout(this.connectRetryTimer);
            this.connectRetryTimer = null;
        }
        this.leaveQuickplay();
        this.client.disconnect();
    }

    /** Cancel pending disconnect if opponent is clearly still connected. */
    private clearDisconnectGrace(): void {
        if (this.disconnectGraceTimer) {
            clearTimeout(this.disconnectGraceTimer);
            this.disconnectGraceTimer = null;
            if (import.meta.env.DEV) console.log('[NET] Disconnect grace cleared — opponent still connected');
        }
    }

    // ─── Lobby State Machine ────────────────────────────────────────────────

    /**
     * Call each frame to handle lobby state transitions.
     * Detects when lobbyAction or lobbyStatus changes and triggers network actions.
     */
    updateLobby(): void {
        const mp = this.state.multiplayer;
        if (!mp) {
            // Clean up if multiplayer was just disabled
            if (this.prevLobbyAction !== null) {
                this.client.disconnect();
                this.prevLobbyAction = null;
                this.prevLobbyStatus = null;
            }
            return;
        }

        // Detect transition to 'create'
        if (mp.lobbyAction === 'create' && this.prevLobbyAction !== 'create') {
            this.createRoom('Player');
        }

        // Detect transition to 'quickplay'
        if (mp.lobbyAction === 'quickplay' && this.prevLobbyAction !== 'quickplay') {
            this.startQuickplay(mp.quickplayName);
        }

        // Detect leaving 'quickplay' (ESC back to choose)
        if (this.prevLobbyAction === 'quickplay' && mp.lobbyAction !== 'quickplay') {
            this.leaveQuickplay();
        }

        // Detect 'join' confirmation (status changes to 'Joining...')
        if (mp.lobbyAction === 'join' && mp.lobbyStatus === 'Joining...' && this.prevLobbyStatus !== 'Joining...') {
            this.joinRoom(mp.roomCodeInput, 'Player');
        }

        // Detect rematch request (game_over screen)
        if (mp.lobbyStatus === 'REMATCH_REQUESTED' && this.prevLobbyStatus !== 'REMATCH_REQUESTED') {
            this.client.send({ type: 'rematch' });
            mp.lobbyStatus = 'Waiting for opponent...';
        }

        // Detect forfeit
        if (mp.lobbyStatus === 'FORFEIT' && this.prevLobbyStatus !== 'FORFEIT') {
            this.client.send({ type: 'forfeit' });
        }

        this.prevLobbyAction = mp.lobbyAction;
        this.prevLobbyStatus = mp.lobbyStatus;
    }

    // ─── Room Management ────────────────────────────────────────────────────

    /** Max connection check attempts before giving up (~10 seconds at 100ms intervals). */
    private static readonly MAX_CONNECT_RETRIES = 100;

    private createRoom(playerName: string): void {
        const roomId = this.generateRoomId();
        this.client.connect(PARTY_HOST, roomId);
        this.waitForConnection(playerName);
    }

    private joinRoom(roomCode: string, playerName: string): void {
        this.client.connect(PARTY_HOST, roomCode.toLowerCase());

        // Store the room code so seed derivation matches the host
        if (this.state.multiplayer) {
            this.state.multiplayer.roomCode = roomCode.toUpperCase();
        }

        this.waitForConnection(playerName);
    }

    /** Poll for connection readiness with bounded retries. */
    private waitForConnection(playerName: string): void {
        // Clear any prior retry timer
        if (this.connectRetryTimer) {
            clearTimeout(this.connectRetryTimer);
            this.connectRetryTimer = null;
        }

        let retries = 0;
        const checkConnection = () => {
            if (this.client.connected) {
                this.connectRetryTimer = null;
                if (import.meta.env.DEV) console.log(`[NET] Sending join after ${retries} retries`);
                this.client.send({ type: 'join', name: playerName });
            } else if (retries < MultiplayerManager.MAX_CONNECT_RETRIES) {
                retries++;
                if (retries % 20 === 0) {
                    if (import.meta.env.DEV) console.log(`[NET] Waiting for connection... retry ${retries}/${MultiplayerManager.MAX_CONNECT_RETRIES}`);
                }
                this.connectRetryTimer = setTimeout(checkConnection, 100);
            } else {
                this.connectRetryTimer = null;
                console.warn('[NET] Connection timed out after', retries, 'retries');
                if (this.state.multiplayer) {
                    this.state.multiplayer.lobbyStatus = 'Connection failed — try again';
                }
            }
        };
        this.connectRetryTimer = setTimeout(checkConnection, 100);
    }

    /** Disconnect and clean up. */
    disconnect(): void {
        this.client.disconnect();
        this.state.multiplayer = null;
    }

    // ─── Quickplay / Matchmaker ─────────────────────────────────────────────

    /** Connect to the matchmaker party and join the queue. */
    private startQuickplay(name: string): void {
        this.leaveQuickplay(); // clean up any prior socket

        this.matchmakerSocket = new PartySocket({
            host: PARTY_HOST,
            room: 'queue',
            party: 'matchmaker',
        });

        this.matchmakerSocket.addEventListener('open', () => {
            if (import.meta.env.DEV) console.log('[NET] Connected to matchmaker');
            this.matchmakerSocket?.send(JSON.stringify({
                type: 'queue_join',
                name,
            }));
        });

        this.matchmakerSocket.addEventListener('message', (event: MessageEvent) => {
            try {
                const msg = JSON.parse(event.data as string);
                if (msg.type === 'match_found') {
                    this.handleMatchFound(msg.roomCode, msg.opponentName);
                } else if (msg.type === 'queue_status') {
                    if (import.meta.env.DEV) console.log('[NET] Queue position:', msg.position);
                }
            } catch (e) {
                console.warn('[NET] Matchmaker parse error:', e);
            }
        });

        this.matchmakerSocket.addEventListener('close', () => {
            if (import.meta.env.DEV) console.log('[NET] Matchmaker disconnected');
        });
    }

    /** Leave the matchmaker queue and close the socket. */
    private leaveQuickplay(): void {
        if (this.matchmakerSocket) {
            try {
                this.matchmakerSocket.send(JSON.stringify({ type: 'queue_leave' }));
            } catch { /* socket may already be closed */ }
            this.matchmakerSocket.close();
            this.matchmakerSocket = null;
        }
    }

    /** Handle match_found from matchmaker — close matchmaker, join game room. */
    private handleMatchFound(roomCode: string, opponentName: string): void {
        const mp = this.state.multiplayer;
        // Guard: only handle if still in quickplay mode
        if (!mp || mp.lobbyAction !== 'quickplay') {
            if (import.meta.env.DEV) console.log('[NET] match_found ignored — no longer in quickplay');
            return;
        }

        if (import.meta.env.DEV) console.log(`[NET] Match found! Room: ${roomCode}, opponent: ${opponentName}`);

        // Join the game room FIRST, then close matchmaker after a delay.
        // Safari iOS can fail to open a new WebSocket immediately after closing
        // another to the same host — the old connection isn't fully released yet.
        mp.lobbyStatus = 'Connecting...';
        this.joinRoom(roomCode, mp.quickplayName);

        // Delay matchmaker cleanup so it doesn't race with the game room open
        setTimeout(() => {
            this.leaveQuickplay();
        }, 1000);
    }

    // ─── Frame-Level Integration ────────────────────────────────────────────

    /**
     * Get the appropriate InputProvider for this frame.
     * Returns localInput on your turn, networkInput on opponent's turn.
     */
    getInputForFrame(): InputProvider {
        if (!this.state.multiplayer?.enabled) return localInput;

        // Disconnect overlay — always use local input so remaining player can navigate
        if (this.state.multiplayer.disconnectPhase) return localInput;

        // During non-gameplay phases (game_over, menus, etc.), always use local input
        // so both players can interact with UI (rematch, leave, etc.)
        const gameplayPhases = ['aiming', 'firing', 'awaiting_next_turn', 'death_sequence', 'di_window', 'burst_window', 'defensive_movement', 'edgeguard'];
        if (!gameplayPhases.includes(this.state.phase)) return localInput;

        // During DI window, the active player is diActivePlayer (the hit player),
        // not currentPlayer (the firing player)
        if (this.state.phase === 'di_window') {
            const isDIMyTurn = this.state.diActivePlayer === this.state.multiplayer.mySlot;
            return isDIMyTurn ? localInput : this.networkInput;
        }

        // During burst window, authority is burstActivePlayer — may be currentPlayer (offensive burst)
        // or defenseActivePlayer (defensive burst from recovery phase)
        if (this.state.phase === 'burst_window') {
            const isBurstMyTurn = this.state.burstActivePlayer === this.state.multiplayer.mySlot;
            return isBurstMyTurn ? localInput : this.networkInput;
        }

        // During defensive movement, the active player is defenseActivePlayer (the hit player)
        if (this.state.phase === 'defensive_movement') {
            const isDefenseMyTurn = this.state.defenseActivePlayer === this.state.multiplayer.mySlot;
            return isDefenseMyTurn ? localInput : this.networkInput;
        }

        // During edgeguard, authority is currentPlayer (the attacker who fired)
        if (this.state.phase === 'edgeguard') {
            const isEdgeguardMyTurn = this.state.currentPlayer === this.state.multiplayer.mySlot;
            return isEdgeguardMyTurn ? localInput : this.networkInput;
        }

        const isMyTurn = this.state.currentPlayer === this.state.multiplayer.mySlot;
        return isMyTurn ? localInput : this.networkInput;
    }

    /**
     * Call BEFORE updatePhase() each frame.
     * Captures aim state so fire_action can send the correct power.
     */
    onPreUpdate(): void {
        // Frame-based mp_waiting → aiming transition (deterministic, replaces setTimeout)
        if (this.mpWaitUntilFrame >= 0 && this.state.phase === 'mp_waiting') {
            if (this.state.frameCount >= this.mpWaitUntilFrame) {
                this.mpWaitUntilFrame = -1;
                // Clear stale hash/snapshot state from previous game
                this.pendingCheckpointId = -1;
                this.bufferedTurnSnapshot = null;
                // Enter round_start so the normal flow runs: round_start → burst_window → aiming
                this.state.phase = 'round_start';
                this.state.roundStartTimer = ROUND_START.DURATION;
                this.state.roundStartFireReleased = false;
            }
        }

        if (!this.state.multiplayer?.enabled) return;
        const isMyTurn = this.state.currentPlayer === this.state.multiplayer.mySlot;
        if (isMyTurn && this.state.phase === 'aiming') {
            const p = this.state.players[this.state.multiplayer.mySlot];
            this.preFireSnapshot = {
                x: p.x, y: p.y,
                angle: p.angle, power: p.power,
            };
        }
    }

    /**
     * Call after updatePhase() each frame.
     * Handles sending input + processing network effects.
     */
    onPostUpdate(fx: GameEffect[]): void {
        // Always run lobby state machine
        this.updateLobby();

        if (!this.state.multiplayer?.enabled) return;

        const isMyTurn = this.state.currentPlayer === this.state.multiplayer.mySlot;
        // Also send key_state during DI window when I'm the active DI player
        const isMyDITurn = this.state.phase === 'di_window'
            && this.state.diActivePlayer === this.state.multiplayer.mySlot;

        // Send key state to opponent during phases where input is consumed.
        // Aiming: movement + charge keys matter. DI window: directional input matters.
        // Firing + awaiting_next_turn: post-fire air control uses arrow/WASD keys.
        const isMyBurstTurn = this.state.phase === 'burst_window'
            && this.state.burstActivePlayer === this.state.multiplayer.mySlot;
        const isMyDefenseTurn = this.state.phase === 'defensive_movement'
            && this.state.defenseActivePlayer === this.state.multiplayer.mySlot;
        const isMyEdgeguardTurn = this.state.phase === 'edgeguard'
            && this.state.currentPlayer === this.state.multiplayer.mySlot;
        const isActiveInputPhase = this.state.phase === 'aiming'
            || this.state.phase === 'firing'
            || this.state.phase === 'awaiting_next_turn'
            || this.state.phase === 'lottery_pick';
        if ((isMyTurn && isActiveInputPhase) || isMyDITurn || isMyBurstTurn || isMyDefenseTurn || isMyEdgeguardTurn) {
            this.client.send({
                type: 'key_state',
                frame: this.state.frameCount,
                keys: captureKeyState(),
            });
        }

        // Recovery air control: non-current tumbling player sends directional input
        if (this.state.phase === 'awaiting_next_turn' && this.state.multiplayer?.enabled) {
            const mySlot = this.state.multiplayer.mySlot;
            if (this.state.currentPlayer !== mySlot) {
                const me = this.state.players[mySlot];
                if (me.alive && me.tumble && me.actionState.tag === 'tumble_recovery' && !me.grounded) {
                    this.client.send({
                        type: 'recovery_key_state',
                        frame: this.state.frameCount,
                        keys: captureKeyState(),
                    });
                }
            }
        }

        // Defender position sync — defender sends authoritative position every frame (60Hz)
        // during edgeguard AND defensive_movement. Corrections flow FROM defender TO attacker
        // (defender is source of truth). Without this, the gap between edgeguard exit and
        // defense_resolve allows tumble drift that can flip platform landings.
        if ((this.state.phase === 'edgeguard' || this.state.phase === 'defensive_movement') && this.state.multiplayer?.enabled) {
            const mySlot = this.state.multiplayer.mySlot;
            // During edgeguard: I'm knocked and not the attacker
            // During defensive_movement: I'm the active defender
            const shouldSend = this.state.phase === 'edgeguard'
                ? (this.state.currentPlayer !== mySlot && this.state.edgeguardKnockedIndices.includes(mySlot))
                : (this.state.defenseActivePlayer === mySlot);
            if (shouldSend) {
                const me = this.state.players[mySlot];
                if (me.alive) {
                    this.client.send({
                        type: 'defender_position',
                        playerIndex: mySlot, x: me.x, y: me.y, vx: me.vx, vy: me.vy,
                    });
                }
            }
        }

        // Send aim preview at reduced rate — during aiming and edgeguard phases.
        // Edgeguard needs aim_update because direct aim (mouse/stick/touch) bypasses
        // key_state and directly mutates player.angle — without aim_update, the opponent
        // never sees the attacker's aim direction change during edgeguard.
        if (isMyTurn && (this.state.phase === 'aiming' || this.state.phase === 'edgeguard')) {
            this.aimSendCounter++;
            if (this.aimSendCounter >= AIM_SEND_INTERVAL) {
                this.aimSendCounter = 0;
                const player = this.state.players[this.state.multiplayer.mySlot];
                const aim = {
                    x: player.x,
                    y: player.y,
                    vx: player.vx,
                    vy: player.vy,
                    angle: player.angle,
                    power: player.power,
                    charging: player.charging,
                    shotTier: player.selectedShotTier,
                    momentum: player.momentum,
                };
                if (aim.x !== this.lastSentAim.x || aim.y !== this.lastSentAim.y ||
                    aim.angle !== this.lastSentAim.angle || aim.power !== this.lastSentAim.power ||
                    aim.shotTier !== this.lastSentAim.shotTier ||
                    Math.abs(aim.momentum - this.lastSentAim.momentum) > 1) {
                    this.client.send({ type: 'aim_update', ...aim });
                    this.lastSentAim = aim;
                }
            }
        }

        // Detect grapple anchor attachment — send exact anchor coords for sync
        if (isMyTurn) {
            const currentGrapplePhase = this.state.grapple.active?.phase ?? null;
            if (currentGrapplePhase === 'retracting' && this.lastGrapplePhase !== 'retracting') {
                const active = this.state.grapple.active!;
                const player = this.state.players[this.state.multiplayer.mySlot];
                this.client.send({
                    type: 'grapple_action',
                    action: 'launch',
                    anchorX: active.anchorX,
                    anchorY: active.anchorY,
                    playerX: player.x,
                    playerY: player.y,
                });
            }
            // Detect grapple end — send cancel so receiving client clears rope
            if (this.lastGrapplePhase !== null && currentGrapplePhase === null) {
                this.client.send({ type: 'grapple_action', action: 'cancel' });
            }
            this.lastGrapplePhase = currentGrapplePhase;
        }

        // Process outbound effects — sends network messages for game events
        processOutboundEffects(fx, this.outboundDeps);
    }

    /** Send a rematch request. */
    requestRematch(): void {
        this.client.send({ type: 'rematch' });
    }

    /** Send a forfeit. */
    forfeit(): void {
        this.client.send({ type: 'forfeit' });
    }

    // ─── Keyboard Text Entry ────────────────────────────────────────────────

    private onKeyboardInput(e: KeyboardEvent): void {
        const mp = this.state.multiplayer;
        if (!mp || this.state.phase !== 'mp_lobby' || mp.lobbyAction !== 'join') return;
        if (mp.lobbyStatus === 'Joining...') return; // Already joining, ignore input

        // Allow A-Z, 0-9 for room code
        if (/^[a-zA-Z0-9]$/.test(e.key) && mp.roomCodeInput.length < 8) {
            mp.roomCodeInput += e.key.toUpperCase();
            e.preventDefault();
        }
        // Backspace
        if (e.key === 'Backspace' && mp.roomCodeInput.length > 0) {
            mp.roomCodeInput = mp.roomCodeInput.slice(0, -1);
            e.preventDefault();
        }
    }

    // ─── Private ────────────────────────────────────────────────────────────

    private buildOutboundDeps(): OutboundDeps {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return {
            client: this.client,
            get debugLog() { return self.debugLog; },
            networkInput: this.networkInput,
            recoveryInput: this.recoveryInput,
            getState: () => this.state,
            getRenderers: () => this.renderers,
            isMyTurn: () => this.state.currentPlayer === this.state.multiplayer!.mySlot,
            getPreFireSnapshot: () => this.preFireSnapshot,
            getPendingRoomSeed: () => this.pendingRoomSeed,
            setPendingRoomSeed: (seed) => { this.pendingRoomSeed = seed; },
            setMpWaitUntilFrame: (frame) => { this.mpWaitUntilFrame = frame; },
            setLastGrapplePhase: (phase) => { this.lastGrapplePhase = phase; },
            getPendingCheckpointId: () => this.pendingCheckpointId,
            setPendingCheckpointId: (id) => { this.pendingCheckpointId = id; },
            applyBufferedSnapshotAndHash: () => {
                if (!this.bufferedTurnSnapshot) return;
                // Snapshot arrived before advanceTurn — now that advanceTurn has run,
                // apply the buffered snapshot and send the deferred hash.
                applyTurnSnapshotData(this.state, this.bufferedTurnSnapshot, this.state.multiplayer!.mySlot, this.debugLog);
                this.bufferedTurnSnapshot = null;
                const hash = hashGameState(this.state);
                this.client.send({
                    type: 'state_hash',
                    checkpointId: this.pendingCheckpointId,
                    hash,
                });
                if (import.meta.env.DEV) console.log(`[NET] Deferred hash sent after buffered snapshot (checkpoint ${this.pendingCheckpointId})`);
                this.pendingCheckpointId = -1;
            },
        };
    }

    private setupClientHandlers(): void {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        // Connection & lifecycle handlers (room_code, room_ready, hash, resync, disconnect, etc.)
        registerConnectionHandlers({
            client: this.client,
            networkInput: this.networkInput,
            get debugLog() { return self.debugLog; },
            getState: () => this.state,
            getRenderers: () => this.renderers,
            getPendingRoomSeed: () => this.pendingRoomSeed,
            setPendingRoomSeed: (seed) => { this.pendingRoomSeed = seed; },
            clearDisconnectGrace: () => this.clearDisconnectGrace(),
            getDisconnectGraceTimer: () => this.disconnectGraceTimer,
            setDisconnectGraceTimer: (timer) => { this.disconnectGraceTimer = timer; },
        });

        // Gameplay relay handlers (key_state, aim_update, fire_action, di/burst/defense resolve, etc.)
        registerGameplayHandlers({
            client: this.client,
            networkInput: this.networkInput,
            recoveryInput: this.recoveryInput,
            get debugLog() { return self.debugLog; },
            getState: () => this.state,
            getRenderers: () => this.renderers,
            clearDisconnectGrace: () => this.clearDisconnectGrace(),
            setPendingRoomSeed: (seed) => { this.pendingRoomSeed = seed; },
            getPendingCheckpointId: () => this.pendingCheckpointId,
            onSnapshotReceived: (snapshot) => {
                if (this.pendingCheckpointId >= 0) {
                    // Normal path: advanceTurn already ran, apply snapshot + hash immediately
                    applyTurnSnapshotData(this.state, snapshot, this.state.multiplayer!.mySlot, this.debugLog);
                    const hash = hashGameState(this.state);
                    this.client.send({
                        type: 'state_hash',
                        checkpointId: this.pendingCheckpointId,
                        hash,
                    });
                    if (import.meta.env.DEV) console.log(`[NET] Deferred hash sent after snapshot (checkpoint ${this.pendingCheckpointId})`);
                    this.pendingCheckpointId = -1;
                    this.bufferedTurnSnapshot = null;
                } else {
                    // Race condition: snapshot arrived before advanceTurn ran.
                    // Buffer it — will be applied after advanceTurn when turn_boundary_checkpoint fires.
                    this.bufferedTurnSnapshot = snapshot;
                    if (import.meta.env.DEV) console.log(`[NET] Snapshot arrived before checkpoint — buffering for post-advanceTurn application`);
                }
            },
        });
    }

    private generateRoomId(): string {
        const chars = 'abcdefghjkmnpqrstuvwxyz23456789'; // no ambiguous chars (0/o, 1/l/i)
        let id = '';
        const randomValues = new Uint32Array(4);
        globalThis.crypto.getRandomValues(randomValues);
        for (let i = 0; i < 4; i++) {
            id += chars[randomValues[i] % chars.length];
        }
        return id;
    }
}
